/**
 * The declaring side of the 2D light pass: what a light, a lit item and a light mesh say about
 * themselves. Each declaration is an effect or a ref: it unwinds on unmount, and a provider
 * `setState` from a child's render is an update during render.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { LIGHT_PASS_LAYER } from './lightPassLayers.js';
import { useCanvasLighting2D } from './lightPassContext.js';
/**
 * Declares a light while `enabled` holds, until a value changes or unmount. It depends on the
 * values, not the object a light rebuilds each render, which would hand it a new ordinal. Returns
 * the ordinal, or null while the light is undeclared.
 */
export function useRegisterCanvasLight2D(enabled, declaration) {
    const { registerLight } = useCanvasLighting2D();
    const [ordinal, setOrdinal] = useState(null);
    const { itemCullMask, zMin, zMax, layerMin, layerMax } = declaration.reach;
    const { sequence, slot, shadowItemCullMask, tintsShadow, mixes } = declaration;
    useEffect(() => {
        if (!enabled) {
            setOrdinal(null);
            return undefined;
        }
        const registration = registerLight({
            reach: { itemCullMask, zMin, zMax, layerMin, layerMax },
            sequence,
            slot,
            shadowItemCullMask,
            tintsShadow,
            mixes,
        });
        setOrdinal(registration.ordinal);
        return registration.release;
    }, [
        enabled,
        registerLight,
        itemCullMask,
        zMin,
        zMax,
        layerMin,
        layerMax,
        sequence,
        slot,
        shadowItemCullMask,
        tintsShadow,
        mixes,
    ]);
    return ordinal;
}
/**
 * Declares a lit item at `placement`, by value, with the accumulation it reads. The cap hands
 * `takenLights` as a new array only when they change, so its identity serves.
 */
export function useRegisterLitItem(placement, read) {
    const { registerItem } = useCanvasLighting2D();
    const { lightMask, z, layer, takenLights } = placement;
    useEffect(() => registerItem({ lightMask, z, layer, takenLights }, read), [registerItem, lightMask, z, layer, takenLights, read]);
}
/**
 * Hands the per-item cap an item at `placement`, uncapped, whose own geometry `geometry` holds.
 * `take` receives the item's light list while more than 15 lights meet it or the render
 * budget drops one. The cap records a new entry as handed nothing, so each release hands the item
 * null to match: else an item that moves to a placement that fits every light keeps the old list.
 */
export function useCapItemLights(placement, geometry, take) {
    const { registerCappedItem } = useCanvasLighting2D();
    const { lightMask, z, layer } = placement;
    useEffect(() => {
        const release = registerCappedItem({
            placement: { lightMask, z, layer, takenLights: null },
            geometry,
            take,
        });
        return () => {
            release();
            take(null);
        };
    }, [registerCappedItem, lightMask, z, layer, geometry, take]);
}
/**
 * A callback ref that hands one of light `ordinal`'s meshes to the pass as `role`. The mesh moves
 * onto the light pass layer, which the main render never draws, and stays hidden until a pass
 * shows it: a mesh the pass does not hold would otherwise draw into every list. `onMesh` receives
 * the mesh, and null on unmount.
 */
export function usePassMeshRef(ordinal, role, onMesh) {
    const { registerPassMesh } = useCanvasLighting2D();
    const held = useRef(null);
    return useCallback((mesh) => {
        if (held.current) {
            held.current.release();
            held.current.mesh.visible = false;
            held.current = null;
        }
        if (mesh) {
            mesh.layers.set(LIGHT_PASS_LAYER);
            mesh.visible = false;
            if (ordinal !== null)
                held.current = { mesh, release: registerPassMesh(mesh, ordinal, role) };
        }
        onMesh?.(mesh);
    }, [registerPassMesh, ordinal, role, onMesh]);
}
//# sourceMappingURL=lightPassDeclarations.js.map